import {
  Briefcase,
  Building2,
  FolderOpen,
  Gauge,
  LineChart,
  ScrollText,
  Settings,
  Users
} from 'lucide-react';
import type { Action, PlatformSubject } from '@bliks-io/contracts';

import type { NavMainItem, NavSubItem } from '@/layout/nav/NavMain';

/**
 * Ein Eintrag der Plattform-Navigation, mit der Ressource, an der dort
 * gearbeitet wird (ADR 0042).
 *
 * Sichtbar, wer sie lesen darf — dieselbe Frage, die der Server an jedem
 * Endpunkt dahinter stellt. `detailAction` ist, was eine Unterseite verlangt,
 * wo das mehr ist als lesen: der Vertrieb sieht die Liste der
 * Arbeitsbereiche, aber keinen von innen.
 */
export interface AdminNavItem extends NavMainItem {
  subject: PlatformSubject;
  detailAction?: Action;
  items?: NavSubItem[];
}

/**
 * Nach Arbeit gruppiert, nicht nach Tabellen.
 *
 * Vorher waren es neun flache Einträge, je einer pro Sammlung — und die
 * Arbeit eines Vertrieblers kreuzte davon drei, während der Eintrag mit
 * seinem Namen das Wichtigste nicht konnte.
 */
export const adminNavigation: AdminNavItem[] = [
  {
    title: 'common.Sidebar.AccountManagement',
    url: '/accounts',
    icon: Briefcase,
    subject: 'PlatformAccount'
  },
  {
    title: 'common.Sidebar.TenantManagement',
    url: '/tenants',
    icon: Building2,
    subject: 'PlatformTenant',
    detailAction: 'readSensitive'
  },
  {
    title: 'common.Sidebar.UserManagement',
    url: '/users',
    icon: Users,
    subject: 'PlatformUser'
  },
  {
    title: 'common.Sidebar.PartnerContent',
    url: '/partner-content',
    icon: FolderOpen,
    subject: 'PlatformPartnerContent'
  },
  {
    title: 'common.Sidebar.AuditLogs',
    url: '/audit-logs',
    icon: ScrollText,
    subject: 'PlatformInsights'
  },
  {
    title: 'common.Sidebar.Analytics',
    url: '/analytics',
    icon: LineChart,
    subject: 'PlatformInsights'
  },
  {
    title: 'common.Sidebar.AiUsage',
    url: '/ai-usage',
    icon: Gauge,
    subject: 'PlatformInsights'
  },
  {
    title: 'common.Sidebar.Settings',
    url: '/settings',
    icon: Settings,
    subject: 'PlatformSettings'
  }
];

/**
 * Was dieser Mensch sehen darf.
 *
 * Gezeigt wird, was erlaubt ist — abgelehnt wird trotzdem am Server. Eine
 * Regel, die nur der Browser kennt, ist keine.
 */
export function visibleAdminNavigation(
  items: AdminNavItem[],
  can: (action: Action, subject: PlatformSubject) => boolean
): NavMainItem[] {
  return items.filter((item) => can('read', item.subject));
}

/**
 * Was eine Adresse unterhalb von `/admin` verlangt.
 *
 * Dieselbe Tabelle wie oben, von der anderen Seite gelesen: die Navigation
 * fragt „was zeige ich", der Routenwächter fragt „darf der hier sein". Ohne
 * das war die Navigation die einzige Tür — und jeder Link, jedes Lesezeichen
 * und jede eingetippte Adresse ging daran vorbei.
 *
 * `null` für `/admin` selbst: der Einstieg entscheidet nach Rolle, wohin es
 * weitergeht, und dürfte nicht seinerseits weitergeleitet werden.
 *
 * Eine Seite, die in keinem Eintrag steht, verlangt alles — wie im Backend
 * ein Endpunkt ohne `@PlatformPolicy` (ADR 0042). Sonst stünde jede neue
 * Seite ohne Navigationseintrag jeder Rolle offen, und erst ihre Abfragen
 * prallten am Server ab.
 *
 * Der längste passende Eintrag gewinnt, damit ein künftiges `/tenants-xy`
 * nicht unter `/tenants` fiele.
 */
export function platformRequirementForAdminPath(
  pathname: string
): { action: Action; subject: PlatformSubject | 'all' } | null {
  const rest = pathname.replace(/^\/admin/, '');
  if (rest === '' || rest === '/') return null;
  let match: AdminNavItem | null = null;
  for (const item of adminNavigation) {
    if (!item.url) continue;
    const hit = rest === item.url || rest.startsWith(`${item.url}/`);
    if (hit && (!match || item.url.length > (match.url?.length ?? 0))) match = item;
  }
  if (!match) return { action: 'manage', subject: 'all' };
  const isDetail = rest !== match.url;
  return {
    action: isDetail ? (match.detailAction ?? 'read') : 'read',
    subject: match.subject
  };
}
